import { HttpException } from '../exceptions/index';
import { QueryParams, QueryParamsBuilder } from './query-param-builder.util';
import { buildRequestTimeoutSignal } from './request-timeout-signal.util';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * A handler returning `null` (e.g. `GET /companies/selected` for a company the caller can no
 * longer see) answers 200 with an empty body and no JSON content type. Handing callers the raw
 * `''` back is worse than nothing: it is defined, so `value?.field` does NOT short-circuit and
 * every downstream optional chain reads through to `undefined.field` and throws.
 */
export const emptyBodyToUndefined = (text: string): string | undefined =>
  text === '' ? undefined : text;

export class HttpService {
  private queryParamsBuilder = new QueryParamsBuilder();

  public buildQueryParam(url: string, queryParams?: QueryParams) {
    if (!queryParams) return url;
    return `${url}${this.queryParamsBuilder.buildQuery(queryParams)}`;
  }

  protected async _delete<T, B extends BodyInit | null | undefined>(
    url: string,
    { body, queryParams, accessToken }: { accessToken: string; body?: B; queryParams: QueryParams },
  ) {
    return this.requestWithAuth<T>(url, {
      init: {
        method: 'DELETE',
        body,
      },
      queryParams,
      accessToken,
    });
  }

  protected async _get<T>(
    url: string,
    { accessToken, queryParams }: { accessToken?: string; queryParams: QueryParams },
  ) {
    return this.requestWithAuth<T>(url, { init: undefined, accessToken, queryParams });
  }

  protected async _patch<T, B extends BodyInit | null | undefined>(
    url: string,
    { body, queryParams, accessToken }: { accessToken?: string; body: B; queryParams: QueryParams },
  ) {
    return this.requestWithAuth<T>(url, {
      init: { method: 'PATCH', body },
      accessToken,
      queryParams,
    });
  }

  protected async _post<T, B extends BodyInit | null | undefined>(
    url: string,
    { body, queryParams, accessToken }: { accessToken?: string; body: B; queryParams: QueryParams },
  ) {
    return this.requestWithAuth<T>(url, {
      init: { method: 'POST', body },
      accessToken,
      queryParams,
    });
  }

  protected async request<T>(url: string, init?: RequestInit | undefined) {
    let res: Response;
    const signal = buildRequestTimeoutSignal(init?.signal ?? undefined);

    try {
      res = await fetch(url, { ...init, signal });
    } catch (e) {
      console.error(e);
      throw e;
    }

    const contentType = res.headers.get('Content-Type') || '';
    let body: unknown;

    if (contentType.includes('application/json')) {
      try {
        body = await res.json();
      } catch {
        body = emptyBodyToUndefined(await res.text());
      }
    } else {
      body = emptyBodyToUndefined(await res.text());
    }

    if (!res.ok) {
      const errorBody = typeof body === 'string' || isRecord(body) ? body : undefined;
      throw new HttpException(res, errorBody);
    }

    return body as T;
  }

  protected async requestWithAuth<T>(
    url: string,
    {
      accessToken,
      init,
      queryParams,
    }: {
      accessToken?: string;
      init?: RequestInit | undefined;
      queryParams?: QueryParams;
    } = {},
  ) {
    return this.request<T>(this.buildQueryParam(url, queryParams), {
      credentials: 'include',
      ...init,
      headers: { Authorization: `Bearer ${accessToken}`, ...init?.headers },
    });
  }
}

const httpService = new HttpService();

export default httpService;
